import React from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Grid
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import GeoXCommunityTrends from '../../geographic_explorer/community/GeoXCommunityTrends';
import FormattedNarrative, { SectionRenderer } from './FormattedNarrative';
import DebugPanel from './DebugPanel';
import DataSourcesInfo from './DataSourcesInfo';

// Helper function to format grade arrays as ranges
const formatGradeRange = (grades) => {
  if (!grades || grades.length === 0) return 'N/A';
  
  // Filter out PK and UG as these are excluded from K-12 analysis
  const filteredGrades = grades.filter(g => g !== 'PK' && g !== 'UG');
  
  if (filteredGrades.length === 0) return 'N/A';
  
  // Handle single grade
  if (filteredGrades.length === 1) {
    const grade = filteredGrades[0];
    if (grade === 'Kindergarten' || grade === 'KG') return 'K';
    return grade.replace(/^0+/, '');
  }
  
  // Convert grades to a standard format and sort them properly
  const normalizeGrade = (grade) => {
    if (grade === 'Kindergarten' || grade === 'KG') return { value: 0, display: 'K' };
    const num = parseInt(grade);
    if (!isNaN(num)) return { value: num, display: String(num) };
    return { value: 999, display: grade };
  };
  
  const normalizedGrades = filteredGrades.map(normalizeGrade);
  const sortedGrades = normalizedGrades.sort((a, b) => a.value - b.value);
  
  const firstGrade = sortedGrades[0].display;
  const lastGrade = sortedGrades[sortedGrades.length - 1].display;
  
  return `${firstGrade}-${lastGrade}`;
};

const GeographicSummaryTab = ({ 
  summary, 
  loading, 
  visualizationLoading = false,
  onGenerate, 
  onRegenerate, 
  schoolName, 
  selectedSchool, 
  showAdminControls = true, 
  showDataSources = true 
}) => {
  // Extract fields from summary (works for both live and stored+data formats)
  const { data, narrative, from_cache, tokens_used, mode, generated_at } = summary || {};
  
  // Memoize population data to prevent chart re-renders
  // Must be called before any early returns (React hooks rule)
  const populationTotals = React.useMemo(() => {
    if (!data?.population) return null;
    return {
      past: data.population.population_2020,
      current: data.population.population_current,
      future: data.population.population_2030
    };
  }, [data?.population]);

  const totalChange = React.useMemo(() => 
    data?.population?.change_past_pct || 0,
    [data?.population?.change_past_pct]
  );

  // Memoize cardProps to prevent re-creating object on each render
  const chartCardProps = React.useMemo(() => ({
    elevation: 0,
    sx: {
      mb: 0,
      height: '100%',
      backgroundColor: '#ffffff',
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 2,
      position: 'relative',
      overflow: 'hidden',
      '&::before': {
        content: '""',
        position: 'absolute',
        left: 0,
        top: 0,
        bottom: 0,
        width: 4,
        backgroundColor: '#673ab7'
      }
    }
  }), []);

  // Only show full loading if we don't have summary data yet
  if (loading && !summary) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Generating geographic summary...</Typography>
      </Box>
    );
  }

  if (!summary) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No geographic summary generated yet
        </Typography>
        <Button
          variant="contained"
          onClick={onGenerate}
          startIcon={<RefreshIcon />}
          sx={{ mt: 2 }}
        >
          Generate Geographic Summary
        </Button>
      </Box>
    );
  }

  if (summary.error) {
    return (
      <Alert severity="error" action={
        <Button color="inherit" size="small" onClick={onGenerate}>
          Retry
        </Button>
      }>
        {summary.error}
      </Alert>
    );
  }

  // Extract specific sections from narrative
  const extractSection = (sectionTitle) => {
    if (!narrative) return null;
    
    // Check if narrative is a string (markdown format)
    if (typeof narrative === 'string') {
      const parts = narrative.split(/^## /gm).filter(Boolean);
      for (const part of parts) {
        const lines = part.split('\n');
        const title = lines[0].trim();
        if (title === sectionTitle) {
          const content = lines.slice(1).join('\n').trim();
          return { title, content };
        }
      }
    }
    
    return null;
  };

  // Extract all sections in the desired order
  const executiveSummarySection = extractSection('Executive Summary');
  const populationTrendsSection = extractSection('Population Trends');
  const enrollmentContextSection = extractSection('Enrollment Context');
  const competitiveLandscapeSection = extractSection('Competitive Landscape');
  
  // Sections to exclude from FormattedNarrative (we render them manually in order)
  const excludedSections = ['Executive Summary', 'Population Trends', 'Enrollment Context', 'Competitive Landscape'];

  return (
    <Box>
      {/* Regenerate Button */}
      {onRegenerate && (
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            onClick={onRegenerate}
            startIcon={<RefreshIcon />}
            size="small"
          >
            Regenerate
          </Button>
        </Box>
      )}
      
      {/* Mode Indicator - Admin only */}
      {showAdminControls && mode && (
        <Alert 
          severity={mode === 'openai' ? 'success' : (mode === 'stored' ? 'info' : 'warning')} 
          sx={{ mb: 2 }}
        >
          <strong>
            {mode === 'openai' 
              ? '✅ OpenAI API Active - Real AI Analysis' 
              : (mode === 'stored'
                  ? '📁 Loaded from Database - Fresh Data + Stored AI Narrative'
                  : '⚠️ MOCK MODE - OpenAI API Not Configured')}
          </strong>
          {mode === 'mock' && (
            <Box component="span" sx={{ display: 'block', mt: 0.5, fontSize: '0.9em' }}>
              Using placeholder narrative for testing. Configure OpenAI API key to enable real AI analysis.
            </Box>
          )}
        </Alert>
      )}

      {/* Metadata - Admin only */}
      {showAdminControls && (
        <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {from_cache && (
            <Chip label="From Cache" color="info" size="small" />
          )}
          {mode === 'stored' && (
            <Chip label="Stored AI Narrative + Fresh Data" color="success" size="small" />
          )}
          {mode === 'openai' && (
            <Chip label="Live AI Generation" color="primary" size="small" />
          )}
          {tokens_used > 0 && (
            <Chip label={`${tokens_used} tokens used`} size="small" variant="outlined" />
          )}
          {generated_at && (
            <Chip 
              label={`Generated: ${new Date(generated_at).toLocaleString()}`} 
              size="small" 
              variant="outlined" 
            />
          )}
        </Box>
      )}

      {/* Data Sources */}
      {showDataSources && summary.data_sources && (
        <DataSourcesInfo sources={[
          { label: "Population Data", value: summary.data_sources.population },
          { label: "School Enrollment", value: summary.data_sources.enrollment },
          { label: "Nearby Schools", value: summary.data_sources.nearby_schools }
        ]} />
      )}

      {/* 1. Executive Summary - First */}
      {executiveSummarySection && (
        <SectionRenderer 
          section={executiveSummarySection}
          showExplorerLink={!showAdminControls}
          selectedSchool={selectedSchool}
          summaryType="geographic"
        />
      )}

      {/* 2. Population Trends Section with Chart Side-by-Side */}
      {populationTrendsSection && (
        <Grid container spacing={3} sx={{ mb: 2 }}>
          <Grid item xs={12} md={data?.population ? 6 : 12}>
            <SectionRenderer 
              section={populationTrendsSection} 
              sx={{ mb: 0, height: '100%' }}
              showExplorerLink={!showAdminControls}
              selectedSchool={selectedSchool}
              summaryType="geographic"
            />
          </Grid>
          {populationTotals && (
            <Grid item xs={12} md={6}>
              {visualizationLoading ? (
                <Paper
                  elevation={0}
                  sx={{
                    mb: 0,
                    height: '100%',
                    minHeight: 350,
                    backgroundColor: '#ffffff',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    position: 'relative',
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      bottom: 0,
                      width: 4,
                      backgroundColor: '#673ab7'
                    }
                  }}
                >
                  <Box sx={{ textAlign: 'center' }}>
                    <CircularProgress size={32} sx={{ mb: 2 }} />
                    <Typography variant="body2" color="text.secondary">
                      Loading chart...
                    </Typography>
                  </Box>
                </Paper>
              ) : (
                <GeoXCommunityTrends
                  populationTotals={populationTotals}
                  totalChange={totalChange}
                  selectedDriveTime={10}
                  height={350}
                  cardProps={chartCardProps}
                />
              )}
            </Grid>
          )}
        </Grid>
      )}

      {/* 3. Enrollment Context Section */}
      {enrollmentContextSection && (
        <SectionRenderer 
          section={enrollmentContextSection}
          showExplorerLink={!showAdminControls}
          selectedSchool={selectedSchool}
          summaryType="geographic"
        />
      )}

      {/* 4. Competitive Landscape Section with Combined Visualizations Side-by-Side */}
      {competitiveLandscapeSection && (
        <Grid container spacing={3} sx={{ mb: 2 }}>
          <Grid item xs={12} md={(data?.nearby_schools || data?.county_charter_comparison) ? 6 : 12}>
            <SectionRenderer 
              section={competitiveLandscapeSection} 
              sx={{ mb: 0, height: '100%' }}
              showExplorerLink={!showAdminControls}
              selectedSchool={selectedSchool}
              summaryType="geographic"
            />
          </Grid>
          {(data?.nearby_schools || data?.county_charter_comparison) && (
            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  mb: 0,
                  height: '100%',
                  backgroundColor: '#ffffff',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: 4,
                    backgroundColor: '#673ab7'
                  }
                }}
              >
                {/* Nearby Schools Summary Stats */}
                {data?.nearby_schools && data.nearby_schools.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Nearby Schools Summary
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                    Overlapping Grades and 100+ Students
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" color="primary">
                          {data.nearby_schools.length}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Schools in Area
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" color="primary">
                          {Math.round(
                            data.nearby_schools.reduce((sum, s) => sum + s.enrollment_current, 0) / data.nearby_schools.length
                          ).toLocaleString()}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Average Enrollment
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" color={
                          data.nearby_schools.filter(s => s.enrollment_change_pct > 0).length > data.nearby_schools.length / 2 
                            ? 'success.main' 
                            : 'error.main'
                        }>
                          {data.nearby_schools.filter(s => s.enrollment_change_pct > 0).length}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Schools with Increasing Enrollment
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              )}

              {/* County Charter Comparison */}
              {data?.county_charter_comparison && data.county_charter_comparison.count > 0 && (
                <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    County Charter Comparison
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                    {data.county_charter_comparison.county_name} Charter Schools
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" color="primary">
                          {data.county_charter_comparison.count}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Charter Schools (100+ Students, Overlapping Grades)
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" color={
                          data.county_charter_comparison.median_enrollment_change_pct > 0
                            ? 'success.main'
                            : 'error.main'
                        }>
                          {data.county_charter_comparison.median_enrollment_change_pct > 0 ? '+' : ''}
                          {data.county_charter_comparison.median_enrollment_change_pct}%
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Median Enrollment Change (2019-2020 to 2024-2025, Overlapping Grades)
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              )}
            </Paper>
          </Grid>
          )}
        </Grid>
      )}

      {/* New or Expanding Schools Table - Separate Row */}
      {data?.new_expanding_schools && data.new_expanding_schools.length > 0 && (
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 2,
            backgroundColor: '#ffffff',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: 4,
              backgroundColor: '#673ab7'
            }
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            New or Expanding Schools
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            Since 2019 • K-12 Only
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>School Name</strong></TableCell>
                  <TableCell><strong>Governance</strong></TableCell>
                  <TableCell align="right"><strong>Enrollment</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell align="center"><strong>Grades</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.new_expanding_schools.map((school, index) => (
                  <TableRow key={index}>
                    <TableCell>{school.name}</TableCell>
                    <TableCell>{school.governance}</TableCell>
                    <TableCell align="right">{school.enrollment_current.toLocaleString()}</TableCell>
                    <TableCell>
                      <Chip 
                        label={school.status === 'new' ? 'New School' : 'Expanding'} 
                        color={school.status === 'new' ? 'success' : 'primary'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      {school.status === 'new' 
                        ? formatGradeRange(school.grades_2023)
                        : `${formatGradeRange(school.grades_2018)} → ${formatGradeRange(school.grades_2023)}`
                      }
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Any remaining sections not explicitly rendered above */}
      <FormattedNarrative 
        narrative={narrative} 
        excludeSections={excludedSections}
        showExplorerLinks={!showAdminControls}
        selectedSchool={selectedSchool}
        summaryType="geographic"
      />

      {/* Debug Panel - Admin only */}
      {showAdminControls && (
        <DebugPanel debug={summary.debug} data={summary.data} mode={mode} />
      )}
    </Box>
  );
};

export default GeographicSummaryTab;

